package com.merkost.handpass.sharedweb.components

import androidx.compose.runtime.Composable
import com.merkost.handpass.sharedweb.theme.Accent
import com.merkost.handpass.sharedweb.theme.AccentHover
import com.merkost.handpass.sharedweb.theme.AccentRing
import com.merkost.handpass.sharedweb.theme.AnimNames
import com.merkost.handpass.sharedweb.theme.Border
import com.merkost.handpass.sharedweb.theme.Danger
import com.merkost.handpass.sharedweb.theme.Dur
import com.merkost.handpass.sharedweb.theme.Ease
import com.merkost.handpass.sharedweb.theme.HandpassSpacing
import com.merkost.handpass.sharedweb.theme.Info
import com.merkost.handpass.sharedweb.theme.OnSurface
import com.merkost.handpass.sharedweb.theme.OnSurfaceMuted
import com.merkost.handpass.sharedweb.theme.RadiusControl
import com.merkost.handpass.sharedweb.theme.Shadow
import com.merkost.handpass.sharedweb.theme.Success
import com.merkost.handpass.sharedweb.theme.Surface
import com.merkost.handpass.sharedweb.theme.SurfaceMuted
import com.merkost.handpass.sharedweb.theme.TouchTarget
import com.merkost.handpass.sharedweb.theme.Transparent
import com.merkost.handpass.sharedweb.theme.WebIconSize
import com.merkost.handpass.sharedweb.theme.WebMotion
import com.merkost.handpass.sharedweb.theme.WebSize
import com.merkost.handpass.sharedweb.theme.WebType
import com.merkost.handpass.sharedweb.theme.transitionSurfaceAction
import com.varabyte.kobweb.compose.css.Animation
import com.varabyte.kobweb.compose.css.AnimationIterationCount
import com.varabyte.kobweb.compose.css.Cursor
import org.jetbrains.compose.web.css.AnimationFillMode
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.animation
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderLeft
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.boxShadow
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.scale
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.extendedBy
import com.varabyte.kobweb.silk.style.selectors.active
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLButtonElement

enum class WebToastKind { Success, Error, Info }

val WebToastStyle = CssStyle.base {
    Modifier
        .backgroundColor(Surface.value())
        .border(WebSize.P1.value(), LineStyle.Solid, Border.value())
        .borderRadius(RadiusControl.value())
        .styleModifier {
            property("box-shadow", Shadow.Dropdown)
            property("box-sizing", "border-box")
            property("width", "min(${WebSize.P360.value()}, calc(100vw - ${WebSize.P32.value()}))")
        }
        .padding(topBottom = WebSize.P13.value(), leftRight = HandpassSpacing.Default)
        .display(DisplayStyle.Flex)
        .alignItems(AlignItems.Center)
        .gap(HandpassSpacing.MD)
        .fontSize(WebType.Body)
        .fontWeight(500)
        .animation(
            Animation.of(
                name = AnimNames.ToastIn,
                duration = Dur.Slow,
                timingFunction = Ease.Standard,
                iterationCount = AnimationIterationCount.of(1),
                fillMode = AnimationFillMode.Forwards,
            )
        )
}

val WebToastButtonStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.LegacyInlineFlex)
            .alignItems(AlignItems.Center)
            .justifyContent(JustifyContent.Center)
            .minHeight(TouchTarget.value())
            .border(WebSize.P0.value(), LineStyle.None, Border.value())
            .borderRadius(RadiusControl.value())
            .backgroundColor(Transparent.value())
            .cursor(Cursor.Pointer)
            .fontWeight(700)
            .transitionSurfaceAction()
            .styleModifier {
                property("font-family", "inherit")
                property("font-size", "inherit")
                property("line-height", "inherit")
            }
    }
    active {
        Modifier.scale(WebMotion.PressScale)
    }
    focusVisible {
        Modifier
            .outline(WebSize.P0.value(), LineStyle.None, Transparent.value())
            .boxShadow(spreadRadius = WebSize.P3.value(), color = AccentRing.value())
    }
}

val WebToastActionStyle = WebToastButtonStyle.extendedBy {
    base {
        Modifier
            .color(Accent.value())
            .padding(leftRight = HandpassSpacing.SM)
    }
    hover {
        Modifier
            .backgroundColor(SurfaceMuted.value())
            .color(AccentHover.value())
    }
}

val WebToastDismissStyle = WebToastButtonStyle.extendedBy {
    base {
        Modifier
            .color(OnSurfaceMuted.value())
            .minWidth(TouchTarget.value())
            .height(TouchTarget.value())
            .padding(WebSize.P0.value())
    }
    hover {
        Modifier
            .backgroundColor(SurfaceMuted.value())
            .color(OnSurface.value())
    }
}

private fun WebToastKind.accentColor(): CSSColorValue = when (this) {
    WebToastKind.Success -> Success.value()
    WebToastKind.Error -> Danger.value()
    WebToastKind.Info -> Info.value()
}

private fun WebToastKind.icon(): WebIcon = when (this) {
    WebToastKind.Success -> WebIcon.Check
    WebToastKind.Error -> WebIcon.Close
    WebToastKind.Info -> WebIcon.Info
}

@Composable
fun WebToast(
    message: String,
    kind: WebToastKind,
    actionLabel: String? = null,
    exiting: Boolean = false,
    onAction: () -> Unit = {},
    onDismiss: () -> Unit = {},
) {
    Div(
        attrs = WebToastStyle.toModifier()
            .borderLeft(WebSize.P3.value(), LineStyle.Solid, kind.accentColor())
            .let {
                if (exiting) {
                    it.animation(
                        Animation.of(
                            name = AnimNames.ToastOut,
                            duration = Dur.Normal,
                            timingFunction = Ease.Standard,
                            iterationCount = AnimationIterationCount.of(1),
                            fillMode = AnimationFillMode.Forwards,
                        )
                    )
                } else {
                    it
                }
            }
            .toAttrs {
                attr("role", if (kind == WebToastKind.Error) "alert" else "status")
                attr("aria-live", if (kind == WebToastKind.Error) "assertive" else "polite")
                attr("aria-atomic", "true")
            }
    ) {
        Span(
            attrs = Modifier
                .color(kind.accentColor())
                .display(DisplayStyle.Grid)
                .styleModifier {
                    property("place-items", "center")
                    property("flex-shrink", "0")
                }
                .toAttrs()
        ) {
            Icon(kind.icon(), size = WebIconSize.Md)
        }
        Span(
            attrs = Modifier
                .color(OnSurface.value())
                .styleModifier {
                    property("flex", "1")
                    property("min-width", "0")
                    property("text-wrap", "pretty")
                }
                .toAttrs()
        ) {
            Text(message)
        }
        actionLabel?.let { label ->
            Button(
                attrs = WebToastActionStyle.toAttrs<AttrsScope<HTMLButtonElement>> {
                    attr("type", "button")
                    style { property("white-space", "nowrap") }
                    onClick { onAction() }
                }
            ) {
                Text(label)
            }
        }
        Button(
            attrs = WebToastDismissStyle.toAttrs<AttrsScope<HTMLButtonElement>> {
                attr("type", "button")
                attr("aria-label", "Dismiss notification")
                onClick { onDismiss() }
            }
        ) {
            Icon(WebIcon.Close, size = WebIconSize.Sm)
        }
    }
}
